import { company, socials } from '@awpmarine/core/company';
import { AwpMarineLogo, InstagramV2Logo, LinkedInV2Logo, YouTubeLogo } from '@awpmarine/ui/components/logos';
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@awpmarine/ui/components/ui/item';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@awpmarine/ui/components/ui/navigation-menu';
import { Sheet, SheetContent, SheetTitle } from '@awpmarine/ui/components/ui/sheet';
import { Link, useRouterState } from '@tanstack/react-router';
import { cn } from 'cn';
import {
  AnchorIcon,
  ArrowRightIcon,
  AwardIcon,
  BriefcaseIcon,
  FileTextIcon,
  GlobeIcon,
  MailIcon,
  MenuIcon,
  PhoneIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import * as React from 'react';

import '@/lib/dash0';
import { LanguageMenu } from '@/components/language';
import { env } from '@/env';
import { serviceCategories } from '@/lib/services';
import { m } from '@/paraglide/messages';
import type { MessageFunction } from '@/paraglide/runtime';
import type { FileRouteTypes } from '@/routeTree.gen';

type NavHref = FileRouteTypes['to'];

// Message functions, read when the header renders so the text follows the page's locale.
const navLinks: { href: NavHref; label: MessageFunction; description: MessageFunction; icon: typeof AnchorIcon }[] = [
  { href: '/services', label: m.header_services, description: m.header_services_description, icon: AnchorIcon },
  { href: '/about', label: m.header_about, description: m.header_about_description, icon: AwardIcon },
  { href: '/team', label: m.header_team, description: m.header_team_description, icon: UsersIcon },
  { href: '/news', label: m.header_news, description: m.header_news_description, icon: FileTextIcon },
  { href: '/careers', label: m.header_careers, description: m.header_careers_description, icon: BriefcaseIcon },
];

const dashboardUrl = new URL('/dashboard', env.VITE_APP_URL).href;

export function Header() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isActive = (href: NavHref) => pathname.startsWith(href);

  return (
    <header className='border-line border-b bg-white'>
      <div className='mx-auto flex max-w-310 items-center justify-between gap-6 px-5 py-4 sm:px-8 sm:py-5'>
        <Link to='/' aria-label={m.header_home_label()}>
          <AwpMarineLogo className='h-9 w-auto sm:h-10' />
        </Link>

        <NavigationMenu aria-label={m.header_nav_label()} className='hidden lg:flex'>
          <NavigationMenuList className='gap-7'>
            <NavigationMenuItem>
              <NavigationMenuTrigger
                className={cn(
                  'h-auto bg-transparent p-0 text-base whitespace-nowrap hover:bg-transparent hover:text-brand focus:bg-transparent data-popup-open:bg-transparent data-popup-open:text-brand data-popup-open:hover:bg-transparent',
                  isActive('/services') ? 'font-bold text-brand' : 'font-medium',
                )}
              >
                {m.header_services()}
              </NavigationMenuTrigger>
              <NavigationMenuContent className='w-130 p-3'>
                <ul className='flex flex-col gap-1'>
                  {serviceCategories.map((category) => (
                    <li key={category.slug}>
                      <NavigationMenuLink
                        closeOnClick
                        render={<Link to='/services' hash={category.slug} />}
                        className='items-start gap-3.5 rounded-xl p-3 text-base'
                      >
                        <span className='bg-mist flex size-10 shrink-0 items-center justify-center rounded-lg text-brand'>
                          <category.icon className='size-5' />
                        </span>
                        <span className='min-w-0'>
                          <span className='block font-bold'>{category.title()}</span>
                          <span className='text-subtle block text-sm/snug'>{category.tagline()}</span>
                        </span>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
                <div className='border-line mt-2 flex items-center justify-between gap-3 border-t px-3 pt-3 pb-1'>
                  <NavigationMenuLink
                    closeOnClick
                    render={<Link to='/services' hash='where-we-work' />}
                    className='gap-2 p-0 text-sm font-semibold hover:bg-transparent hover:text-brand focus:bg-transparent'
                  >
                    <GlobeIcon className='size-4 text-brand' />
                    {m.header_where_we_work()}
                  </NavigationMenuLink>
                  <NavigationMenuLink
                    closeOnClick
                    render={<Link to='/services' />}
                    className='gap-1.5 p-0 text-sm font-semibold text-brand hover:bg-transparent focus:bg-transparent'
                  >
                    {m.header_all_services()}
                    <ArrowRightIcon className='size-4' />
                  </NavigationMenuLink>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
            {navLinks
              .filter((link) => link.href !== '/services')
              .map((link) => (
                <NavigationMenuItem key={link.href}>
                  <NavigationMenuLink
                    closeOnClick
                    render={<Link to={link.href} aria-current={isActive(link.href) ? 'page' : undefined} />}
                    className={cn(
                      'p-0 text-base whitespace-nowrap transition-colors hover:bg-transparent hover:text-brand focus:bg-transparent',
                      isActive(link.href) ? 'font-bold text-brand' : 'font-medium',
                    )}
                  >
                    {link.label()}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
          </NavigationMenuList>
        </NavigationMenu>

        <div className='flex items-center gap-2.5'>
          <LanguageMenu short className='hidden lg:flex' />
          <div
            role='group'
            aria-label={m.header_actions_label()}
            className='flex overflow-hidden rounded-full bg-brand'
          >
            <Link
              to='/contact'
              className='flex min-h-11 items-center px-5 font-semibold whitespace-nowrap text-white transition-colors hover:bg-white/10 sm:pr-5 sm:pl-6'
            >
              {m.header_contact()}
            </Link>
            <a
              href={dashboardUrl}
              target='_blank'
              rel='noopener noreferrer'
              aria-label={m.header_sign_in_label()}
              title={m.header_sign_in()}
              className='hidden min-h-11 w-12 items-center justify-center border-l border-white/35 text-white transition-colors hover:bg-white/10 md:flex'
            >
              <UserIcon className='size-5' />
            </a>
          </div>
          <button
            type='button'
            aria-label={m.header_open_menu()}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
            className='flex size-11 items-center justify-center rounded-full border border-[#cfd7e2] lg:hidden'
          >
            <MenuIcon className='size-5' />
          </button>
        </div>
      </div>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side='right'
          showCloseButton={false}
          // svh is the height with the browser's toolbars showing, so Safari's floating bar cannot cover the bottom.
          className='flex flex-col gap-0 overflow-y-auto p-0 data-[side=right]:h-svh data-[side=right]:w-full data-[side=right]:border-l-0 data-[side=right]:sm:max-w-sm data-[side=right]:sm:border-l'
        >
          <SheetTitle className='sr-only'>{m.header_menu()}</SheetTitle>
          <div className='border-line flex items-center justify-between border-b px-5 py-3.5'>
            <AwpMarineLogo className='h-8 w-auto' />
            <div className='flex items-center gap-2'>
              {/* In the top bar, since the menu has no room for a row of its own. */}
              <LanguageMenu />
              <button
                type='button'
                aria-label={m.header_close_menu()}
                onClick={() => setMenuOpen(false)}
                className='flex size-11 items-center justify-center rounded-full border border-[#cfd7e2]'
              >
                <XIcon className='size-5' />
              </button>
            </div>
          </div>

          <nav aria-label={m.header_nav_label()} className='flex flex-col gap-0.5 px-2 pt-3'>
            {navLinks.map((link) => (
              <Item
                key={link.href}
                render={
                  <Link
                    to={link.href}
                    onClick={() => setMenuOpen(false)}
                    aria-current={isActive(link.href) ? 'page' : undefined}
                  />
                }
                className={cn(
                  'gap-3.5 rounded-2xl border-0 p-3 text-base [a]:hover:bg-transparent',
                  isActive(link.href) && 'bg-[#eef3fa]',
                )}
              >
                <ItemMedia
                  className={cn(
                    'size-11 translate-y-0 self-center rounded-xl text-brand group-has-data-[slot=item-description]/item:translate-y-0 group-has-data-[slot=item-description]/item:self-center',
                    isActive(link.href) ? 'bg-white' : 'bg-mist',
                  )}
                >
                  <link.icon className='size-5' />
                </ItemMedia>
                <ItemContent className='min-w-0 gap-0'>
                  <ItemTitle className={cn('text-lg/normal font-bold', isActive(link.href) && 'text-[#1f4a80]')}>
                    {link.label()}
                  </ItemTitle>
                  <ItemDescription className='line-clamp-none text-sm'>{link.description()}</ItemDescription>
                </ItemContent>
              </Item>
            ))}
          </nav>

          <div className='grid grid-cols-2 gap-2 px-5 pt-3.5'>
            <Link
              to='/contact'
              onClick={() => setMenuOpen(false)}
              className='bg-mist rounded-xl px-3.5 py-3 font-semibold'
            >
              {m.header_menu_contact()}
            </Link>
            <Link
              to='/legal'
              onClick={() => setMenuOpen(false)}
              className='bg-mist rounded-xl px-3.5 py-3 font-semibold'
            >
              {m.header_menu_legal()}
            </Link>
          </div>

          <div className='px-5 pt-4 pb-5'>
            <a
              href={dashboardUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center justify-center gap-2 rounded-full border border-[#cfd7e2] p-3 font-semibold'
            >
              <UserIcon className='size-4.5' />
              {m.header_menu_sign_in()}
            </a>
          </div>

          <div className='bg-navy mt-auto px-5 pt-4.5 pb-6 text-white'>
            <p className='text-on-navy mb-2.5 text-sm'>{m.header_get_in_touch()}</p>
            <div className='mb-4 grid grid-cols-2 gap-2'>
              <a
                href={`tel:${company.phones[0].tel}`}
                className='text-navy flex items-center justify-center gap-2 rounded-full bg-white p-3 font-bold'
              >
                <PhoneIcon className='size-4.5' />
                {m.header_call()}
              </a>
              <a
                href={`mailto:${company.emails.default}`}
                className='flex items-center justify-center gap-2 rounded-full border border-[#3a4c68] p-3 font-bold'
              >
                <MailIcon className='size-4.5' />
                {m.header_email()}
              </a>
            </div>
            <SocialLinks className='border-[#3a4c68]' />
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}

/** Round links to the company's social accounts; shared by the mobile menu and the footer. */
export function SocialLinks({ className }: { className?: string }) {
  const links = [
    { href: socials.linkedin.url, label: 'LinkedIn', Logo: LinkedInV2Logo },
    { href: socials.instagram.url, label: 'Instagram', Logo: InstagramV2Logo },
    { href: socials.youtube.url, label: 'YouTube', Logo: YouTubeLogo },
  ];

  return (
    <div className='flex gap-2.5'>
      {links.map(({ href, label, Logo }) => (
        <a
          key={label}
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={m.social_link_label({ network: label })}
          className={cn(
            'flex size-11 items-center justify-center rounded-full border border-[#33496b] text-white transition-colors hover:bg-white/10',
            className,
          )}
        >
          <Logo className='size-4.5' />
        </a>
      ))}
    </div>
  );
}
